/**
 * Analytics payload builders for EV coverage manual-edit interactions.
 */
import { Demographics } from "../../../data-model/medicalPaOrdersModel";
import {
  ManualEvBvAmountType,
  ManualEvBvFieldKey,
  ManualEvBvSubscriberKind,
  ManualEvEditClickSource,
  ManualEvEvFieldKey,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";

/** Context shared by EV coverage analytics events (per active insurance tier). */
export interface EvCoverageAnalyticsContext {
  demographics: Demographics | undefined;
  orderId: string;
  orgId: string;
  userId: string;
  userEmail: string;
  payerName: string;
  /** Primary, secondary, etc. for the active coverage tab. */
  coverageType: string;
}

const buildBaseFields = (ctx: EvCoverageAnalyticsContext) => ({
  patient_id: ctx.demographics?.patient_id ?? "",
  order_id: ctx.orderId,
  org_id: ctx.orgId,
  user_id: ctx.userId,
  user_email: ctx.userEmail,
});

const buildConcatPatientName = (demographics: Demographics | undefined) =>
  demographics?.first_name + " " + demographics?.last_name;

/**
 * User begins editing populated EV coverage (plan summary Edit or Update Manually).
 *
 * @param ctx - Order, user, payer, and tier context
 * @param source - Which control opened edit mode
 * @returns BigQuery worker payload for {@link EventsName.MANUAL_EV_EDIT_CLICKED}
 */
export const buildManualEvEditClickedEvent = (
  ctx: EvCoverageAnalyticsContext,
  source: ManualEvEditClickSource,
) => ({
  ...buildBaseFields(ctx),
  event_name: EventsName.MANUAL_EV_EDIT_CLICKED,
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildConcatPatientName(ctx.demographics),
    patient_mrn: ctx.demographics?.patient_id ?? "",
    payer_name: ctx.payerName,
    coverage_type: ctx.coverageType,
    source,
  },
});

/**
 * User saves manual overrides to auto-fetched EV coverage data.
 *
 * @param ctx - Order, user, payer, and tier context
 * @returns BigQuery worker payload for {@link EventsName.MANUAL_EV_OVERRIDE_SAVED}
 */
export const buildManualEvOverrideSavedEvent = (
  ctx: EvCoverageAnalyticsContext,
) => ({
  ...buildBaseFields(ctx),
  event_name: EventsName.MANUAL_EV_OVERRIDE_SAVED,
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildConcatPatientName(ctx.demographics),
    patient_mrn: ctx.demographics?.patient_id ?? "",
    payer_name: ctx.payerName,
    coverage_type: ctx.coverageType,
  },
});

/** EV plan-summary field edit payload for {@link EventsName.MANUAL_EV_FIELD_EDITED}. */
export interface ManualEvEvFieldEditedDetail {
  fieldCategory: "ev";
  fieldKey: ManualEvEvFieldKey;
  valueEntered: string;
}

/** BV financial field edit payload for {@link EventsName.MANUAL_EV_FIELD_EDITED}. */
export interface ManualEvBvFieldEditedDetail {
  fieldCategory: "bv";
  fieldKey: ManualEvBvFieldKey;
  amountType?: ManualEvBvAmountType;
  subscriber?: ManualEvBvSubscriberKind;
  /** Service type code for the active treatment tab (empty when unknown). */
  serviceTypeCode: string;
  valueEntered: string;
}

/** Discriminated union passed from editable EV/BV controls. */
export type ManualEvFieldEditedDetail =
  | ManualEvEvFieldEditedDetail
  | ManualEvBvFieldEditedDetail;

/**
 * User finished editing a plan-summary or BV field (debounced at the call site).
 *
 * @param ctx - Order, user, payer, and tier context
 * @param detail - EV or BV field key and entered value
 * @returns BigQuery worker payload for {@link EventsName.MANUAL_EV_FIELD_EDITED}
 */
export const buildManualEvFieldEditedEvent = (
  ctx: EvCoverageAnalyticsContext,
  detail: ManualEvFieldEditedDetail,
) => {
  const shared = {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildConcatPatientName(ctx.demographics),
    patient_mrn: ctx.demographics?.patient_id ?? "",
    payer_name: ctx.payerName,
    coverage_type: ctx.coverageType,
    field_category: detail.fieldCategory,
    value_entered: detail.valueEntered,
  };

  if (detail.fieldCategory === "ev") {
    return {
      ...buildBaseFields(ctx),
      event_name: EventsName.MANUAL_EV_FIELD_EDITED,
      additional_data: {
        ...shared,
        field_key: detail.fieldKey,
      },
    };
  }

  return {
    ...buildBaseFields(ctx),
    event_name: EventsName.MANUAL_EV_FIELD_EDITED,
    additional_data: {
      ...shared,
      field_key: detail.fieldKey,
      ...(detail.amountType ? { amount_type: detail.amountType } : {}),
      ...(detail.subscriber ? { subscriber: detail.subscriber } : {}),
      service_type_code: detail.serviceTypeCode,
    },
  };
};
